import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { offerService } from '../../../services/offerService';

const OfferCard = ({ 
  offer, 
  onEdit, 
  onDelete, 
  onNotesUpdate,
  isDragging = false,
  dragHandleProps = {},
  canDelete = true
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState(offer?.internalNotes || '');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleSaveNotes = () => {
    onNotesUpdate(offer?.id, notesValue);
    setIsEditingNotes(false);
  };

  const handleCancelNotes = () => {
    setNotesValue(offer?.internalNotes || '');
    setIsEditingNotes(false);
  };

  const getFinancingBadgeColor = (type) => {
    const colors = {
      'Cash': 'bg-success/10 text-success',
      'Conventional': 'bg-primary/10 text-primary',
      'FHA': 'bg-accent/10 text-accent',
      'VA': 'bg-secondary/10 text-secondary',
      'Other': 'bg-muted text-muted-foreground'
    };
    return colors?.[type] || colors?.['Other'];
  };

  const handleViewPdf = async () => {
    if (!offer?.contractPdfUrl) return;

    setLoadingPdf(true);
    const { data, error } = await offerService?.getContractPdfUrl(offer?.contractPdfUrl);
    setLoadingPdf(false);

    if (error) {
      console.error('Error getting PDF URL:', error);
      return;
    }

    if (data) {
      // Open PDF in new tab
      window.open(data, '_blank');
    }
  };

  return (
    <div 
      className={`bg-card rounded-lg border border-border transition-all duration-250 ${
        isDragging ? 'shadow-elevation-xl opacity-50' : 'shadow-elevation-sm hover:shadow-elevation-md'
      }`}
    >
      {/* Card Header */}
      <div className="p-3 md:p-4 border-b border-border">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <button
              {...dragHandleProps}
              className="mt-1 p-1.5 hover:bg-muted rounded-lg transition-colors cursor-grab active:cursor-grabbing flex-shrink-0"
              aria-label="Drag to reorder"
            >
              <Icon name="GripVertical" size={20} className="text-muted-foreground" />
            </button>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="text-lg md:text-xl font-semibold text-foreground">
                  {offer?.buyerName}
                </h3>
                <span className={`px-2.5 py-0.5 rounded-full text-xs md:text-sm font-medium ${getFinancingBadgeColor(offer?.financingType)}`}>
                  {offer?.financingType}
                </span>
              </div>
              
              <div className="flex items-center gap-4 text-sm md:text-base text-muted-foreground flex-wrap">
                <div className="flex items-center gap-2">
                  <Icon name="Calendar" size={16} />
                  <span>Submitted {formatDate(offer?.submittedDate)}</span>
                </div>
                {offer?.expirationDate && (
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={16} />
                    <span>Expires {formatDate(offer?.expirationDate)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(offer?.id)}
              iconName="Edit2"
            />
            {canDelete && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(offer?.id)}
                iconName="Trash2"
              />
            )}
          </div>
        </div>
      </div>
      {/* Key Terms */}
      <div className="p-3 md:p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        <div>
          <div className="text-xs md:text-sm text-muted-foreground mb-0.5">Total Purchase Price</div>
          <div className="text-lg font-semibold text-foreground">
            {formatCurrency(offer?.offerPrice)}
          </div>
        </div>

        <div>
          <div className="text-xs md:text-sm text-muted-foreground mb-0.5">Initial Deposit</div>
          <div className="text-lg font-semibold text-foreground">
            {formatCurrency(offer?.downPayment)}
            <span className="text-sm text-muted-foreground ml-2">
              ({((offer?.downPayment / offer?.offerPrice) * 100)?.toFixed(1)}%)
            </span>
          </div>
        </div>

        <div>
          <div className="text-xs md:text-sm text-muted-foreground mb-0.5">Additional Deposit</div>
          <div className="text-lg font-semibold text-foreground">
            {formatCurrency(offer?.earnestMoney)}
          </div>
        </div>

        <div>
          <div className="text-xs md:text-sm text-muted-foreground mb-0.5">Mortgage Amount</div>
          <div className="text-lg font-semibold text-foreground">
            {formatCurrency(offer?.preApprovalAmount || 0)}
          </div>
        </div>

        <div>
          <div className="text-xs md:text-sm text-muted-foreground mb-0.5">Balance due at Purchase</div>
          <div className="text-lg font-semibold text-foreground">
            {formatCurrency(offer?.balanceOfPurchase || 0)}
          </div>
        </div>

        <div>
          <div className="text-xs md:text-sm text-muted-foreground mb-0.5">Closing Date</div>
          <div className="text-lg font-semibold text-foreground">
            {formatDate(offer?.closingDate)}
          </div>
        </div>

        <div>
          <div className="text-xs md:text-sm text-muted-foreground mb-0.5">Contingencies</div>
          <div className="flex flex-wrap gap-2">
            {offer?.contingencies?.length > 0 ? (
              offer?.contingencies?.map((contingency, index) => (
                <span 
                  key={index}
                  className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs md:text-sm"
                >
                  {contingency}
                </span>
              ))
            ) : (
              <span className="text-sm text-muted-foreground">None</span>
            )}
          </div>
        </div>
      </div>
      {/* Expandable Details */}
      {isExpanded && (
        <div className="px-3 md:px-4 pb-3 md:pb-4 border-t border-border">
          <div className="pt-3 md:pt-4 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {(offer?.appraisalContingency !== null && offer?.appraisalContingency !== undefined) && (
              <div>
                <div className="text-xs md:text-sm text-muted-foreground mb-1">Appraisal Contingency</div>
                <div className="text-sm md:text-base text-foreground">{offer?.appraisalContingency} days</div>
              </div>
            )}

            {(offer?.inspectionPeriod !== null && offer?.inspectionPeriod !== undefined) && (
              <div>
                <div className="text-xs md:text-sm text-muted-foreground mb-1">Inspection Period</div>
                <div className="text-sm md:text-base text-foreground">{offer?.inspectionPeriod} days</div>
              </div>
            )}

            {(offer?.sellerConcessions !== null && offer?.sellerConcessions !== undefined) && (
              <div>
                <div className="text-xs md:text-sm text-muted-foreground mb-0.5">Seller Concessions</div>
                <div className="text-lg font-semibold text-foreground">{formatCurrency(offer?.sellerConcessions)}</div>
              </div>
            )}

            {offer?.buyerAgent && (
              <div>
                <div className="text-xs md:text-sm text-muted-foreground mb-1">Buyer's Agent</div>
                <div className="text-sm md:text-base text-foreground">{offer?.buyerAgent}</div>
              </div>
            )}

            {(offer?.preApprovalAmount !== null && offer?.preApprovalAmount !== undefined) && (
              <div>
                <div className="text-xs md:text-sm text-muted-foreground mb-0.5">Pre-Approval Amount</div>
                <div className="text-lg font-semibold text-foreground">{formatCurrency(offer?.preApprovalAmount)}</div>
              </div>
            )}

            {offer?.escalationClause && (
              <div className="md:col-span-2">
                <div className="text-xs md:text-sm text-muted-foreground mb-1">Escalation Clause</div>
                <div className="text-sm md:text-base text-foreground">{offer?.escalationClause}</div>
              </div>
            )}

            {offer?.contractPdfUrl && (
              <div className="md:col-span-2">
                <div className="text-xs md:text-sm text-muted-foreground mb-1.5">Contract Document</div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleViewPdf}
                  disabled={loadingPdf}
                  className="flex items-center gap-2"
                >
                  <Icon name={loadingPdf ? "Loader2" : "FileText"} size={16} className={loadingPdf ? "animate-spin" : ""} />
                  {loadingPdf ? 'Loading PDF...' : 'View Contract PDF'}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Internal Notes */}
      <div className="px-3 md:px-4 pb-3 md:pb-4 border-t border-border">
        <div className="pt-3 md:pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Icon name="FileText" size={16} className="text-muted-foreground" />
              <span className="text-sm md:text-base font-medium text-foreground">Internal Notes</span>
            </div>
            {!isEditingNotes && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditingNotes(true)}
                iconName="Edit2"
                iconPosition="left"
              >
                Edit
              </Button>
            )}
          </div>

          {isEditingNotes ? (
            <div className="space-y-2">
              <textarea
                value={notesValue}
                onChange={(e) => setNotesValue(e?.target?.value)}
                placeholder="Add internal notes about this offer..."
                className="w-full min-h-[100px] px-3 py-2 bg-background border border-input rounded-lg text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
              />
              <div className="flex items-center gap-2 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelNotes}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSaveNotes}
                  iconName="Check"
                  iconPosition="left"
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-sm md:text-base text-muted-foreground">
              {offer?.internalNotes || 'No notes added yet'}
            </div>
          )}
        </div>
      </div>
      {/* Expand/Collapse Button */}
      <div className="px-3 md:px-4 pb-3 md:pb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
          iconPosition="right"
          fullWidth
        >
          {isExpanded ? 'Show Less' : 'Show More Details'}
        </Button>
      </div>
    </div>
  );
};

export default OfferCard;